// src/components/cell/CellMenu.jsx
import React, { useState, useContext, useMemo, useRef } from "react";
import axiosInstance from "../../config/axiosInstance";
import { logApiError } from "../../config/apiErrorHandling";
import LicenceModal from "../personal/edit/license/LicenseModal";
import OvertimeModal from "../calendar/edit/OvertimeModal";
import { CalendarContext } from "../context/CalendarContext";
import { useHandleToggleFreeDay } from "../calendar/edit/FreeDay";
import { useHandleAssignManager } from "../calendar/edit/Manager";
import { useHandleTogglePublicHoliday } from "../calendar/edit/PublicHoliday";
import {
  useHandleChangeShift,
  useHandleChangeShiftPeriod,
  useResolveMultiRole,
} from "../calendar/edit/Workshift";
import useConfirm from "./dialogs/UseConfirm";
import { enqueueSnackbar } from "notistack";
import {
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemIcon,
  IconButton,
  CircularProgress,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import FreeBreakfastIcon from "@mui/icons-material/FreeBreakfast";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import HolidayVillageIcon from "@mui/icons-material/HolidayVillage";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import TodayIcon from "@mui/icons-material/Today";
import DateRangeIcon from "@mui/icons-material/DateRange";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import { Divider as MuiDivider } from "@mui/material";
import ConfirmCalendarDialog from "../calendar/dialogs/ConfirmCalendarDialog";

/* ==================== Helper turno: descripción con fallback a horario ==================== */
const formatShiftDesc = (workshift) => {
  if (workshift?.description_workshift) return workshift.description_workshift;
  const from = workshift?.hour_from?.slice(0, 5);
  const to = workshift?.hour_to?.slice(0, 5);
  if (from && to) return `${from} a ${to}`;
  return "No asignado, turno estándar";
};

/* ==================== Helpers multi-rol (cruzando posiciones/calendarios) ==================== */
const fetchMultiOverview = async ({ ci, day, station_id, preloadedItems = null }) => {
  if (Array.isArray(preloadedItems) && preloadedItems.length > 0) {
    return preloadedItems;
  }
  try {
    const { data } = await axiosInstance.get("/calendar/multi_role_overview", {
      params: { ci, day, station_id: String(station_id) },
    });
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
};

const buildCrossCalendarsConfirm = (items = [], actionText, dayStr) => (
  <Stack spacing={2} sx={{ minWidth: 420 }}>
    <Alert severity="info" variant="outlined">
      {actionText} <b>{dayStr}</b>.
    </Alert>

    <Typography variant="overline">ASIGNACIONES DEL DÍA</Typography>
    <List dense sx={{ py: 0 }}>
      {items.map((it, idx) => (
        <ListItem key={idx} sx={{ py: 0.25 }}>
          <ListItemText
            primary={
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={(it.role === "principal" || it.work) ? "Principal" : "Secundario"}
                />
                <Typography variant="body2" fontWeight={600}>
                  {it.workarea_name || "Área"}
                </Typography>
                <Chip size="small" label={it?.workshift?.name || "S/A"} />
              </Stack>
            }
            secondary={it?.workshift?.desc || "No asignado, turno estándar"}
            primaryTypographyProps={{ component: "div" }}
            secondaryTypographyProps={{ variant: "caption", color: "text.secondary" }}
          />
        </ListItem>
      ))}
    </List>
  </Stack>
);

const mustConfirmMultiAcrossCalendars = async ({
  ci,
  day,
  station_id,
  confirmFn,
  actionText,
  preloadedItems = null,
}) => {
  const items = await fetchMultiOverview({ ci, day, station_id, preloadedItems });
  if (items.length > 1) {
    const ok = await confirmFn({
      title: "Confirmar cambio (multi-rol)",
      content: buildCrossCalendarsConfirm(items, actionText, day),
      confirmText: "Confirmar",
      cancelText: "Cancelar",
      maxWidth: "sm",
    });
    return ok;
  }
  return true;
};
/* ============================================================================================= */

const formatCalendarLabel = (calendar) => {
  const position = calendar?.position_name || `Área ${calendar?.position_id ?? "—"}`;
  const start = calendar?.start_date || "—";
  const end = calendar?.end_date || "—";
  return `${position} (${start} a ${end})`;
};

const formatCalendarStatus = (status) => ({
  active: "Activo",
  inactive: "Cerrado",
}[status] || status || "—");

const formatCalendarSecondary = (calendar) => {
  const statusLabel = formatCalendarStatus(calendar?.status);
  const parts = [`Estado: ${statusLabel}`];
  if (Array.isArray(calendar?.conflict_dates) && calendar.conflict_dates.length > 0) {
    parts.push(`Fechas afectadas: ${calendar.conflict_dates.join(", ")}`);
  }
  return parts.join(" · ");
};

const buildDateRangeReplaceScopes = ({ ci, dateFrom, dateTo, periodStart }) => {
  const scopes = [
    { kind: "employee_period", ci: String(ci), category: "rest_between_days" },
  ];

  const start = new Date(`${dateFrom}T00:00:00`);
  const end = new Date(`${dateTo}T00:00:00`);
  for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
    const day = current.toISOString().split("T")[0];
    scopes.push({ kind: "employee_day", ci: String(ci), day });
    scopes.push({ kind: "day", day });
    if (periodStart && day === periodStart) {
      scopes.push({ kind: "employee_period", ci: String(ci), category: "rotation_opening" });
    }
  }

  return scopes;
};

const isBeforeEmploymentStart = (day, firstDay) => {
  if (!day || !firstDay) return false;
  return String(day) < String(firstDay);
};

const CellMenu = ({
  anchorEl,
  onClose,
  day,
  employeeData,
  station_id,
  workarea_id,
  workshifts,
  onBusyChange,
}) => {
  const {
    fetchCalendar,
    calendarData,
    setCalendarData,
    mergeCalendarValidationPatch,
  } = useContext(CalendarContext);
  const {
    ci,
    name,
    surname,
    workshift_manager,
    free_day,
    description_license,
    is_public_holiday,
    work_public_holiday,
    license_id,
    is_manager,
    override_manager,
    multiRoleOverviewItems = [],
    first_day = null,
    employment_locked = false,
  } = employeeData;

  const effectivePositionId =
    employeeData?.id_position ??
    calendarData?.position_id ??
    calendarData?.position ??
    null;
  const isEmploymentLocked = Boolean(
    employment_locked || isBeforeEmploymentStart(day, first_day)
  );
  const employmentLockedMessage = first_day
    ? `No se puede editar esta fecha porque el empleado ingresa el ${first_day}.`
    : "No se puede editar esta fecha porque el empleado aún no ingresó.";

  const [loadingManager, setLoadingManager] = useState(false);
  const [loadingFreeDay, setLoadingFreeDay] = useState(false);
  const [loadingAbsence, setLoadingAbsence] = useState(false);
  const [loadingHoliday, setLoadingHoliday] = useState(false);
  const [loadingPeriodShifts, setLoadingPeriodShifts] = useState({});
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [overtimeModalOpen, setOvertimeModalOpen] = useState(false);
  const [blockedCalendars, setBlockedCalendars] = useState([]);
  const [blockedCalendarsMessage, setBlockedCalendarsMessage] = useState("");
  const [implicatedCalendars, setImplicatedCalendars] = useState([]);
  const [implicatedCalendarsMessage, setImplicatedCalendarsMessage] = useState("");
  const [confirmingImplicated, setConfirmingImplicated] = useState(false);
  const implicatedRetryRef = useRef(null);
  const [anchorSubMenu, setAnchorSubMenu] = useState(null);
  const [anchorSubMenuPeriod, setAnchorSubMenuPeriod] = useState(null);

  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflictPayload, setConflictPayload] = useState(null);
  const [pendingChange, setPendingChange] = useState(null);
  const [inFlight, setInFlight] = useState(false);

  const isStructuralManager = Boolean(is_manager);
  const canManualManagerCoverage = !isStructuralManager && Boolean(override_manager);
  const canCoverNight = Boolean(employeeData?.is_night || employeeData?.override_night);
  const enabledWorkshifts = workshifts.filter((shift) => shift.enabled);
  // "Solo día": no tiene sentido ofrecer "cambiar" al mismo turno que ya tiene la celda.
  const selectableWorkshiftsForDay = enabledWorkshifts.filter(
    (shift) => shift.id !== employeeData.workshift_id
  );
  // "Quincena": el turno de ESTA celda puntual no debe excluirse, porque el resto de
  // los días de la quincena pueden tener otro turno distinto.
  const selectableWorkshiftsForPeriod = enabledWorkshifts;
  const hasRestrictedNightOption = enabledWorkshifts.some(
    (shift) => shift.is_night && !canCoverNight
  );

  const handleToggleFreeDay = useHandleToggleFreeDay();
  const changeShift = useHandleChangeShift();
  const changeShiftPeriod = useHandleChangeShiftPeriod();
  const resolveMultiRole = useResolveMultiRole();
  const handleAssignManager = useHandleAssignManager();
  const handleTogglePublicHoliday = useHandleTogglePublicHoliday();

  const { confirm: confirmMulti, ConfirmDialog: ConfirmDialogMulti } = useConfirm();

  const setBusy = onBusyChange || (() => {});
  const startBusy = (payload) => setBusy(payload);
  const stopBusy = () => setBusy(null);
  const closeBlockedCalendarsDialog = () => {
    setBlockedCalendars([]);
    setBlockedCalendarsMessage("");
  };
  const showBlockedCalendars = (items, message) => {
    if (!Array.isArray(items) || items.length === 0) return;
    setBlockedCalendars(items);
    setBlockedCalendarsMessage(message);
  };
  const closeImplicatedCalendarsDialog = () => {
    setImplicatedCalendars([]);
    setImplicatedCalendarsMessage("");
    implicatedRetryRef.current = null;
  };
  const showImplicatedCalendars = (items, message, retryFn) => {
    if (!Array.isArray(items) || items.length === 0) return;
    setImplicatedCalendars(items);
    setImplicatedCalendarsMessage(message);
    implicatedRetryRef.current = retryFn;
  };
  const handleConfirmImplicatedCalendars = async () => {
    const retryFn = implicatedRetryRef.current;
    closeImplicatedCalendarsDialog();
    if (typeof retryFn === "function") {
      setConfirmingImplicated(true);
      try {
        await retryFn();
      } finally {
        setConfirmingImplicated(false);
      }
    }
  };

  const buildBusyPayload = ({ actionLabel, shiftName = null, scope = "day", dayLabel = day }) => ({
    actionLabel,
    shiftName,
    scope,
    day: dayLabel,
    employeeCi: String(ci),
    employeeName: `${name} ${surname}`.trim(),
  });

  const areasToday = useMemo(() => {
    const out = [];
    
    // Buscar todas las áreas donde está el empleado este día
    for (const wa of calendarData?.workareas || []) {
      const emp = wa?.days?.[day]?.employees?.find((e) => String(e.ci) === String(ci));
      if (!emp) continue;
      
      const areaName = wa.workarea_name || `Área ${wa.workarea_id}`;
      
      // Determinar el rol usando fixed_primary_area de employeeData
      let role = "Secundario";
      if (employeeData?.fixed_primary_area) {
        // Si tiene fixed_primary_area definido, comparar con el nombre del área
        role = areaName === employeeData.fixed_primary_area ? "Principal" : "Secundario";
      } else {
        // Si no tiene fixed_primary_area, el área actual es la principal
        role = Number(wa.workarea_id) === Number(workarea_id) ? "Principal" : "Secundario";
      }
      
      out.push({
        workarea_id: Number(wa.workarea_id),
        name: areaName,
        role: role,
        isCurrent: Number(wa.workarea_id) === Number(workarea_id),
        shiftName: emp?.workshift?.name_workshift || "S/A",
        shiftDesc: formatShiftDesc(emp?.workshift),
      });
    }

    // Si el empleado tiene un área principal fija pero no está en la lista
    // (caso: multi-rol secundario donde no trabaja en su área principal este día),
    // agregarlo como "Principal" con información genérica
    if (employeeData?.fixed_primary_area && !out.some(a => a.role === "Principal")) {
      out.unshift({
        workarea_id: null,
        name: employeeData.fixed_primary_area,
        role: "Principal",
        isCurrent: false,
        shiftName: "No asignado",
        shiftDesc: "No trabaja en esta área este día",
      });
    }

    // Si no encontramos ninguna área (empleado no está en workareas.days.employees),
    // crear una entrada con la información disponible
    if (out.length === 0) {
      let currentAreaName = "Área desconocida";
      
      const currentArea = calendarData?.workareas?.find(
        (wa) => Number(wa.workarea_id) === Number(workarea_id)
      );
      
      if (currentArea?.workarea_name) {
        currentAreaName = currentArea.workarea_name;
      } else if (employeeData?.fixed_primary_area) {
        currentAreaName = employeeData.fixed_primary_area;
      }

      const isPrimary = employeeData?.fixed_primary_area 
        ? currentAreaName === employeeData.fixed_primary_area
        : true;

      out.push({
        workarea_id: Number(workarea_id),
        name: currentAreaName,
        role: isPrimary ? "Principal" : "Secundario",
        isCurrent: true,
        shiftName: employeeData?.workshift?.name_workshift || 
                  employeeData?.workshift || 
                  "S/A",
        shiftDesc: employeeData?.workshift?.description_workshift ||
                  employeeData?.workshift_description ||
                  formatShiftDesc(employeeData?.workshift),
      });

      if (employeeData?.fixed_aux_areas && Array.isArray(employeeData.fixed_aux_areas)) {
        employeeData.fixed_aux_areas.forEach((auxAreaName) => {
          if (!out.some(area => area.name === auxAreaName)) {
            out.push({
              workarea_id: null,
              name: auxAreaName,
              role: "Secundario",
              isCurrent: false,
              shiftName: "S/A",
              shiftDesc: "No asignado, turno estándar",
            });
          }
        });
      }
    }

    return out;
  }, [calendarData, day, ci, workarea_id, employeeData]);

  const getAreasForDialog = async () => areasToday;

  const buildConfirmContent = ({ actionText, day: dayStr, areas, selectedShift = null }) => {
    const principal = areas.find((a) => a.role === "Principal");
    const secundarios = areas.filter((a) => a.role !== "Principal");

    return (
      <Stack spacing={2} sx={{ minWidth: 420 }}>
        <Alert severity="info" variant="outlined">
          {actionText}{" "}
          {selectedShift ? (
            <>
              <b>{selectedShift.name}</b>{" "}
              <Typography component="span" variant="caption" color="text.secondary">
                ({selectedShift.description})
              </Typography>{" "}
              para{" "}
            </>
          ) : null}
          <b>{dayStr}</b>.
        </Alert>

        <Stack spacing={1}>
          <Typography variant="overline">ROL PRINCIPAL</Typography>
          {principal ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={600}>{principal.name}</Typography>
              <Chip size="small" label={principal.shiftName} />
              <Typography variant="caption" color="text.secondary">{principal.shiftDesc}</Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">—</Typography>
          )}
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <Typography variant="overline">ROL SECUNDARIO</Typography>
          {secundarios.length ? (
            <List dense sx={{ py: 0 }}>
              {secundarios.map((s, idx) => (
                <ListItem key={s.workarea_id || idx} sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                        <Chip size="small" label={s.shiftName} />
                      </Stack>
                    }
                    secondary={s.shiftDesc}
                    primaryTypographyProps={{ component: "div" }}
                    secondaryTypographyProps={{ variant: "caption", color: "text.secondary" }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">—</Typography>
          )}
        </Stack>
      </Stack>
    );
  };

  const confirmSummary = (headerLine) => {
    const principal = areasToday.find((a) => a.role === "Principal");
    const secundarios = areasToday.filter((a) => a.role !== "Principal");
    const lines = [
      headerLine,
      "",
      principal
        ? `Rol principal: ${principal.name} — ${principal.shiftName} (${principal.shiftDesc})`
        : "Rol principal: —",
      secundarios.length
        ? "Roles secundarios:\n" +
          secundarios.map((s) => `• ${s.name} — ${s.shiftName} (${s.shiftDesc})`).join("\n")
        : "Roles secundarios: —",
    ];
    return lines.join("\n");
  };

  const isBusy =
    inFlight ||
    loadingManager ||
    loadingFreeDay ||
    loadingAbsence ||
    loadingHoliday ||
    Object.values(loadingPeriodShifts).some(Boolean);

  const holidayStateLabel = !is_public_holiday
    ? null
    : work_public_holiday
      ? "No trabaja feriado"
      : description_license
        ? "No trabaja por inasistencia"
        : free_day
          ? "No trabaja por día libre"
          : employeeData?.work
            ? "Trabaja feriado"
            : "Sin marca especial";

  const holidayActionLabel = work_public_holiday
    ? "Marcar como trabaja feriado"
    : "Marcar como no trabaja feriado";

  const handleCloseMenu = () => {
    if (isBusy) return;
    setAnchorSubMenu(null);
    setAnchorSubMenuPeriod(null);
    onClose();
  };

  /* -------------------- Día libre -------------------- */
  const onClickFreeDay = async (carryForward = true) => {
    const okGlobal = await mustConfirmMultiAcrossCalendars({
      ci,
      day,
      station_id,
      confirmFn: confirmMulti,
      actionText: !free_day
        ? "Se marcará LIBRE en TODAS las áreas/posiciones donde figure este empleado para"
        : "Se quitará LIBRE en TODAS las áreas/posiciones donde figure este empleado para",
      preloadedItems: multiRoleOverviewItems,
    });
    if (!okGlobal) return;

    const areas = await getAreasForDialog();
    const isMulti = areas.length > 1;
    const addingFree = !free_day;

    if (isMulti) {
      const ok = await confirmMulti({
        title: addingFree ? "Confirmar día LIBRE" : "Quitar día LIBRE",
        content: buildConfirmContent({
          actionText: addingFree
            ? "Se marcará LIBRE en TODAS las áreas para"
            : "Se quitará LIBRE en TODAS las áreas para",
          day,
          areas,
        }),
        confirmText: addingFree ? "Confirmar" : "Quitar",
        cancelText: "Cancelar",
        maxWidth: "sm",
      });
      if (!ok) return;
    }

    const result = await handleToggleFreeDay(
      ci, day, free_day, station_id, effectivePositionId, setLoadingFreeDay, onClose, false, carryForward
    );
    if (result?.info?.status === 409 && result.info.needsConfirmation) {
      showImplicatedCalendars(
        result.info.implicatedCalendars,
        "El empleado tiene un calendario activo en la fecha seleccionada. Continuar modificará la planificación actual.",
        () => handleToggleFreeDay(
          ci, day, free_day, station_id, effectivePositionId, setLoadingFreeDay, onClose, true, carryForward
        )
      );
    } else if (result?.info?.status === 409 && result.info.blockedCalendars?.length > 0) {
      showBlockedCalendars(
        result.info.blockedCalendars,
        "No se pudo modificar el día libre porque hay calendarios cerrados para ese empleado en la fecha seleccionada."
      );
    }
  };

  /* -------------------- Feriado trabajado -------------------- */
  const onClickPublicHoliday = async () => {
    const result = await handleTogglePublicHoliday(
      ci, day, is_public_holiday, work_public_holiday,
      station_id, effectivePositionId, setLoadingHoliday, onClose
    );
    if (result?.info?.status === 409 && result.info.needsConfirmation) {
      showImplicatedCalendars(
        result.info.implicatedCalendars,
        "El empleado tiene un calendario activo en la fecha seleccionada. Continuar modificará la planificación actual.",
        () => handleTogglePublicHoliday(
          ci, day, is_public_holiday, work_public_holiday,
          station_id, effectivePositionId, setLoadingHoliday, onClose, true
        )
      );
    } else if (result?.info?.status === 409 && result.info.blockedCalendars?.length > 0) {
      showBlockedCalendars(
        result.info.blockedCalendars,
        "No se pudo modificar el feriado porque hay calendarios cerrados para ese empleado en la fecha seleccionada."
      );
    }
  };

  /* -------------------- Cambiar turno (día) -------------------- */
  const handleShiftChangeClick = async (shiftId) => {
    if (inFlight) return;

    const okGlobal = await mustConfirmMultiAcrossCalendars({
      ci,
      day,
      station_id,
      confirmFn: confirmMulti,
      actionText: "Se asignará el turno en esta posición y se dejará S/A en el resto para",
      preloadedItems: multiRoleOverviewItems,
    });
    if (!okGlobal) return;

    const areas = await getAreasForDialog();
    const isMulti = areas.length > 1;
    const selectedShift = workshifts.find((s) => s.id === shiftId);

    if (isMulti) {
      const ok = await confirmMulti({
        title: "Cambiar turno",
        content: buildConfirmContent({
          actionText: "Se asignará el turno",
          day,
          areas,
          selectedShift,
        }),
        confirmText: "Confirmar",
        cancelText: "Cancelar",
        maxWidth: "sm",
      });
      if (!ok) return;
    }

    setInFlight(true);
    startBusy(buildBusyPayload({
      actionLabel: "Cambiando turno",
      shiftName: selectedShift?.name || null,
      scope: "day",
    }));
    setAnchorSubMenu(null);
    setAnchorSubMenuPeriod(null);
    onClose();

    setPendingChange({
      newShiftId: shiftId,
      ci,
      day,
      currentShiftId: employeeData.workshift_id || null,
      station_id,
      position_id: effectivePositionId,
      workarea_id,
    });

    try {
      const result = await changeShift(
        shiftId, ci, day, employeeData.workshift_id || null,
        station_id, effectivePositionId, workarea_id,
        () => {},
        () => {}
      );
      if (result?.info?.status === 409 && result.info.blockedCalendars?.length > 0) {
        showBlockedCalendars(
          result.info.blockedCalendars,
          "No se pudo cambiar el turno porque hay calendarios cerrados para ese empleado en la fecha seleccionada."
        );
      }
    } catch {
      enqueueSnackbar("Error al cambiar turno", { variant: "error" });
    } finally {
      setInFlight(false);
      stopBusy();
    }
  };

  /* -------------------- Cambiar turno (quincena) -------------------- */
  const periodStart = calendarData?.start_date;
  const periodEnd = calendarData?.end_date;

  const handleShiftChangePeriodClick = async (shiftId) => {
    const okGlobal = await mustConfirmMultiAcrossCalendars({
      ci,
      day,
      station_id,
      confirmFn: confirmMulti,
      actionText: "Se aplicará este turno como base (quincena) en esta posición",
      preloadedItems: multiRoleOverviewItems,
    });
    if (!okGlobal) return;

    const areas = await getAreasForDialog();
    const selectedShift = workshifts.find((s) => s.id === shiftId);

    if (areas.length > 1) {
      const ok = await confirmMulti({
        title: "Cambiar turno (quincena)",
        content: buildConfirmContent({
          actionText: "Se aplicará el turno",
          day: `${periodStart || "—"}–${periodEnd || "—"}`,
          areas,
          selectedShift,
        }),
        confirmText: "Confirmar",
        cancelText: "Cancelar",
        maxWidth: "sm",
      });
      if (!ok) return;
    }

    setLoadingPeriodShifts((p) => ({ ...p, [shiftId]: true }));
    startBusy(buildBusyPayload({
      actionLabel: "Aplicando turno a la quincena",
      shiftName: selectedShift?.name || null,
      scope: "period",
      dayLabel: `${periodStart || "—"}–${periodEnd || "—"}`,
    }));
    setAnchorSubMenu(null);
    setAnchorSubMenuPeriod(null);
    onClose();
    const result = await changeShiftPeriod(
      shiftId,
      ci,
      day,
      station_id,
      effectivePositionId,
      (loading) => setLoadingPeriodShifts((p) => ({ ...p, [shiftId]: loading })),
      () => {
        setAnchorSubMenuPeriod(null);
        onClose();
        stopBusy();
      }
    );
    if (result?.info?.status === 409 && result.info.blockedCalendars?.length > 0) {
      showBlockedCalendars(
        result.info.blockedCalendars,
        "No se pudo aplicar el turno porque hay calendarios cerrados para ese empleado dentro de la quincena seleccionada."
      );
    }
  };

  /* -------------------- Encargado -------------------- */
  const onClickManager = async () => {
    const okGlobal = await mustConfirmMultiAcrossCalendars({
      ci,
      day,
      station_id,
      confirmFn: confirmMulti,
      actionText: (!workshift_manager
        ? "Se asignará cobertura de encargado aquí y se limpiará en otras áreas/posiciones para"
        : "Se quitará la cobertura de encargado para"),
      preloadedItems: multiRoleOverviewItems,
    });
    if (!okGlobal) return;

    const areas = await getAreasForDialog();
    const setOn = !workshift_manager;

    if (areas.length > 1) {
      const ok = await confirmMulti({
        title: setOn ? "Asignar cobertura de encargado" : "Quitar cobertura de encargado",
        content: buildConfirmContent({
          actionText: setOn
            ? "Se asignará cobertura de encargado aquí y se dejará S/A en las otras áreas este día para"
            : "Se quitará la cobertura de encargado para",
          day,
          areas,
        }),
        confirmText: "Confirmar",
        cancelText: "Cancelar",
        maxWidth: "sm",
      });
      if (!ok) return;
    }

    startBusy();
    await handleAssignManager(
      ci, day, setOn, station_id, effectivePositionId, setLoadingManager, onClose
    );
    stopBusy();
  };

  /* -------------------- Inasistencias -------------------- */
  const handleOpenLicenseModal = async () => {
    const areas = await getAreasForDialog();
    if (areas.length > 1) {
      const ok = await confirmMulti({
        title: "Agregar inasistencia",
        content: buildConfirmContent({
          actionText: "Se aplicará en TODAS las áreas para",
          day,
          areas,
        }),
        confirmText: "Confirmar",
        cancelText: "Cancelar",
        maxWidth: "sm",
      });
      if (!ok) return;
    }
    onClose();
    setLicenseModalOpen(true);
  };

  /* -------------------- Horas extra -------------------- */
  const handleOpenOvertimeModal = () => {
    onClose();
    setOvertimeModalOpen(true);
  };

  const handleDeleteAbsence = async (confirmParam = false) => {
    if (!confirmParam) {
      const areas = await getAreasForDialog();
      if (areas.length > 1) {
        const ok = await confirmMulti({
          title: "Quitar inasistencia",
          content: buildConfirmContent({
            actionText: "Se quitará en TODAS las áreas para",
            day,
            areas,
          }),
          confirmText: "Confirmar",
          cancelText: "Cancelar",
          maxWidth: "sm",
        });
        if (!ok) return;
      }
    }

    setLoadingAbsence(true);
    startBusy();
    try {
      const { data } = await axiosInstance.request({
        url: "/calendar/remove_license",
        method: "post",
        params: {
          ci,
          station_id: String(station_id),
          position_id: String(effectivePositionId),
          calendar_id: calendarData?._id || "",
          day,
          ...(license_id ? { license_id: Number(license_id) } : {}),
          ...(confirmParam ? { confirm: true } : {}),
        },
        data: "",
        headers: { "Content-Type": "text/plain" },
      });

      enqueueSnackbar("Inasistencia eliminada", { variant: "success" });
      if (data?.updated_calendar) {
        setCalendarData(data.updated_calendar);
        mergeCalendarValidationPatch({
          errors: data.errors,
          warnings: data.warnings,
          replaceScopes: buildDateRangeReplaceScopes({
            ci,
            dateFrom: data?.changed_range?.date_from || day,
            dateTo: data?.changed_range?.date_to || day,
            periodStart: calendarData?.start_date,
          }),
        });
      } else {
        const inicio = calendarData?.start_date || day;
        await fetchCalendar({ date: inicio, station: station_id, position: effectivePositionId });
      }
    } catch (error) {
      const info = logApiError(
        "license.delete.calendar",
        error,
        "No se pudo quitar la inasistencia. Intenta nuevamente."
      );
      if (info.status === 409 && info.needsConfirmation) {
        showImplicatedCalendars(
          info.implicatedCalendars,
          "El empleado tiene un calendario activo en la fecha seleccionada. Quitar la inasistencia modificará la planificación actual.",
          () => handleDeleteAbsence(true)
        );
        return;
      }
      if (info.status === 409 && info.blockedCalendars.length > 0) {
        showBlockedCalendars(
          info.blockedCalendars,
          "No se pudo quitar la inasistencia porque hay calendarios cerrados para ese empleado en la fecha seleccionada."
        );
      }
      enqueueSnackbar(info.message, { variant: "error" });
    } finally {
      setLoadingAbsence(false);
      onClose();
      stopBusy();
    }
  };

  return (
    <>
      {/* MENU PRINCIPAL */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        MenuListProps={{ dense: true }}
        PaperProps={{ sx: { width: 260, maxWidth: 280, p: 0.5, borderRadius: 2 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", px: 1.25, py: 0.75 }}>
          <Typography variant="subtitle2" fontWeight={700}>Día: {day}</Typography>
          <IconButton size="small" onClick={handleCloseMenu} disabled={isBusy}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 1.25, pb: 0.5 }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            Empleado: {name} {surname}
          </Typography>

          {(() => {
            const p = areasToday.find((a) => a.role === "Principal");
            const s = areasToday.filter((a) => a.role !== "Principal");
            
            return (
              <>
                {/* Rol Principal */}
                {p && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                    <Chip 
                      label="Principal" 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.7rem',
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 600,
                        border: '1px solid #bbdefb'
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {p.name}
                    </Typography>
                    {(p.shiftName === "—" || p.shiftName === "S/A" || p.shiftName === "No asignado") ? (
                      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
                        (no asignado hoy)
                      </Typography>
                    ) : (
                      <Chip 
                        label={p.shiftName} 
                        size="small" 
                        sx={{ 
                          height: 18, 
                          fontSize: '0.7rem',
                          bgcolor: '#e8f5e9',
                          color: '#2e7d32',
                          fontWeight: 500
                        }}
                      />
                    )}
                  </Box>
                )}

                {/* Roles Secundarios */}
                {s.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                    <Chip 
                      label="Secundario" 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.7rem',
                        bgcolor: '#f3e5f5',
                        color: '#7b1fa2',
                        fontWeight: 600,
                        border: '1px solid #e1bee7'
                      }}
                    />
                    {s.map((area, idx) => (
                      <React.Fragment key={idx}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {area.name}
                        </Typography>
                        {(area.shiftName !== "—" && area.shiftName !== "S/A") && (
                          <Chip 
                            label={area.shiftName} 
                            size="small" 
                            sx={{ 
                              height: 18, 
                              fontSize: '0.7rem',
                              bgcolor: '#e8f5e9',
                              color: '#2e7d32',
                              fontWeight: 500
                            }}
                          />
                        )}
                        {idx < s.length - 1 && (
                          <Typography variant="caption" color="text.disabled" sx={{ mx: 0.25 }}>·</Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                )}

                {s.length === 0 && p && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5, fontStyle: 'italic', fontSize: '0.7rem' }}>
                    Sin roles secundarios
                  </Typography>
                )}

                {isStructuralManager && (
                  <Box sx={{ pt: 0.75 }}>
                    <Alert severity="info" variant="outlined" sx={{ py: 0.25 }}>
                      Encargado — turno fijo{p?.shiftName && p.shiftName !== "S/A" && p.shiftName !== "No asignado"
                        ? `: ${p.shiftName} (${p.shiftDesc})`
                        : ", sin turno asignado hoy"}.
                    </Alert>
                  </Box>
                )}
              </>
            );
          })()}
        </Box>

        {is_public_holiday && (
          <Box sx={{ px: 1.25, pb: 0.75 }}>
            <Alert severity="warning" variant="outlined" sx={{ py: 0.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Feriado
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 0.35 }}>
                Estado actual: {holidayStateLabel}.
              </Typography>
            </Alert>
          </Box>
        )}

        <Divider />

        {isEmploymentLocked ? (
          <Box sx={{ px: 1.25, py: 1 }}>
            <Alert severity="info" variant="outlined" sx={{ mb: 0.75 }}>
              {employmentLockedMessage}
            </Alert>
            <Typography variant="caption" color="text.secondary">
              Si hace falta editar estos días, primero hay que ajustar la fecha de ingreso del empleado.
            </Typography>
          </Box>
        ) : (
          <>

            <MenuItem
              onClick={description_license ? handleDeleteAbsence : handleOpenLicenseModal}
              disabled={loadingAbsence || isBusy}
              sx={{ px: 1.25, py: 0.5, fontSize: ".9rem" }}
            >
              <ListItemIcon>
                {loadingAbsence ? <CircularProgress size={20} /> : <EventBusyIcon fontSize="small" />}
              </ListItemIcon>
              {description_license ? "Quitar Inasistencia" : "Agregar Inasistencia"}
            </MenuItem>

            <MenuItem
              onClick={handleOpenOvertimeModal}
              disabled={isBusy}
              sx={{ px: 1.25, py: 0.5, fontSize: ".9rem" }}
            >
              <ListItemIcon>
                <MoreTimeIcon fontSize="small" />
              </ListItemIcon>
              Agregar/Modificar hora extra
            </MenuItem>

            {!is_public_holiday && (
              <Tooltip
                title="Ancla el ciclo de descanso del empleado a partir de este día: las próximas quincenas se calculan tomando este cambio como referencia."
                placement="right"
                enterDelay={400}
              >
                <MenuItem
                  onClick={() => onClickFreeDay(true)}
                  disabled={loadingFreeDay || isBusy}
                  sx={{ px: 1.25, py: 0.5, fontSize: ".85rem", whiteSpace: "normal" }}
                >
                  <ListItemIcon>
                    {loadingFreeDay ? <CircularProgress size={20} /> : <FreeBreakfastIcon fontSize="small" />}
                  </ListItemIcon>
                  {free_day
                    ? "Quitar libre y recalcular secuencia"
                    : "Agregar libre y recalcular secuencia"}
                </MenuItem>
              </Tooltip>
            )}

            {!is_public_holiday && (
              <Tooltip
                title="Cambio puntual: no modifica el ciclo de descanso del empleado ni afecta el cálculo de las próximas quincenas (ej. un trámite de un solo día)."
                placement="right"
                enterDelay={400}
              >
                <MenuItem
                  onClick={() => onClickFreeDay(false)}
                  disabled={loadingFreeDay || isBusy}
                  sx={{ px: 1.25, py: 0.5, fontSize: ".85rem", whiteSpace: "normal" }}
                >
                  <ListItemIcon>
                    {loadingFreeDay ? <CircularProgress size={20} /> : <FreeBreakfastOutlinedIcon fontSize="small" />}
                  </ListItemIcon>
                  {free_day ? "Quitar libre puntual" : "Agregar libre puntual"}
                </MenuItem>
              </Tooltip>
            )}

            {is_public_holiday && !description_license && (
              <MenuItem
                onClick={onClickPublicHoliday}
                disabled={loadingHoliday || isBusy}
                sx={{ px: 1.25, py: 0.5, fontSize: ".9rem" }}
              >
                <ListItemIcon>
                  {loadingHoliday ? <CircularProgress size={20} /> : <HolidayVillageIcon fontSize="small" />}
                </ListItemIcon>
                {holidayActionLabel}
              </MenuItem>
            )}

            {!free_day && !is_public_holiday && !description_license && canManualManagerCoverage && (
              <MenuItem
                onClick={onClickManager}
                disabled={loadingManager || isBusy}
                sx={{ px: 1.25, py: 0.5, fontSize: ".9rem" }}
              >
                <ListItemIcon>
                  {loadingManager ? <CircularProgress size={20} /> : <SupervisorAccountIcon fontSize="small" />}
                </ListItemIcon>
                {workshift_manager ? "Quitar cobertura de encargado" : "Asignar cobertura de encargado"}
              </MenuItem>
            )}

            {!description_license && !free_day && !is_public_holiday && (
              <MenuItem
                onClick={(e) => setAnchorSubMenu(e.currentTarget)}
                disabled={isBusy}
                sx={{ px: 1.25, py: 0.5, fontSize: ".9rem" }}
              >
                <ListItemIcon><TodayIcon fontSize="small" /></ListItemIcon>
                Cambiar turno (solo día)
                <ListItemIcon><ArrowRightIcon fontSize="small" /></ListItemIcon>
              </MenuItem>
            )}

            {!description_license && !free_day && !is_public_holiday && (
              <MenuItem
                onClick={(e) => setAnchorSubMenuPeriod(e.currentTarget)}
                disabled={isBusy}
                sx={{ px: 1.25, py: 0.5, fontSize: ".9rem" }}
              >
                <ListItemIcon><DateRangeIcon fontSize="small" /></ListItemIcon>
                Cambiar turno (quincena)
                <ListItemIcon><ArrowRightIcon fontSize="small" /></ListItemIcon>
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {/* SUBMENÚ: SOLO DÍA */}
      <Menu
        anchorEl={anchorSubMenu}
        open={Boolean(anchorSubMenu) && !isEmploymentLocked}
        onClose={() => setAnchorSubMenu(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem disabled sx={{ opacity: 0.8, fontSize: "0.8rem" }}>
          Sólo {day}
        </MenuItem>
        <MuiDivider />
        {hasRestrictedNightOption && (
          <MenuItem disabled sx={{ opacity: 0.9, fontSize: "0.8rem", whiteSpace: "normal" }}>
            Los turnos nocturnos requieren habilitación nocturna para este empleado.
          </MenuItem>
        )}
        {selectableWorkshiftsForDay.map((shift) => (
            <MenuItem
              key={`day-${shift.id}`}
              onClick={() => handleShiftChangeClick(shift.id)}
              disabled={inFlight || (shift.is_night && !canCoverNight)}
            >
              <ListItemIcon><TodayIcon fontSize="small" /></ListItemIcon>
              {shift.name} ({shift.description})
              {shift.is_night && !canCoverNight ? " - requiere habilitación nocturna" : ""}
            </MenuItem>
          ))}
      </Menu>

      {/* SUBMENÚ: QUINCENA */}
      <Menu
        anchorEl={anchorSubMenuPeriod}
        open={Boolean(anchorSubMenuPeriod) && !isEmploymentLocked}
        onClose={() => setAnchorSubMenuPeriod(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem disabled sx={{ opacity: 0.8, fontSize: "0.8rem", whiteSpace: "nowrap" }}>
          Quincena {periodStart || "—"}–{periodEnd || "—"}
        </MenuItem>
        <MuiDivider />
        {hasRestrictedNightOption && (
          <MenuItem disabled sx={{ opacity: 0.9, fontSize: "0.8rem", whiteSpace: "normal" }}>
            Los turnos nocturnos requieren habilitación nocturna para este empleado.
          </MenuItem>
        )}
        {selectableWorkshiftsForPeriod.map((shift) => (
            <MenuItem
              key={`period-${shift.id}`}
              onClick={() => handleShiftChangePeriodClick(shift.id)}
              disabled={
                Object.values(loadingPeriodShifts).some(Boolean) ||
                (shift.is_night && !canCoverNight)
              }
            >
              <ListItemIcon>
                {loadingPeriodShifts[shift.id] ? <CircularProgress size={20} /> : <DateRangeIcon fontSize="small" />}
              </ListItemIcon>
              {shift.name} ({shift.description})
              {shift.is_night && !canCoverNight ? " - requiere habilitación nocturna" : ""}
            </MenuItem>
          ))}
      </Menu>

      {ConfirmDialogMulti}

      <ConfirmCalendarDialog
        open={conflictOpen}
        title="Conflicto de turnos"
        content={confirmSummary(
          `Al continuar se limpiarán los otros calendarios a S/A y se aplicará el cambio aquí.`
        )}
        confirmText="Continuar"
        cancelText="Cancelar"
        onConfirm={async () => {
          if (inFlight) return;
          setInFlight(true);
          startBusy();
          try {
            const resolveResult = await resolveMultiRole(conflictPayload);
            if (resolveResult?.info?.status === 409 && resolveResult.info.blockedCalendars?.length > 0) {
              showBlockedCalendars(
                resolveResult.info.blockedCalendars,
                "No se pudo resolver el conflicto porque el calendario origen está cerrado."
              );
              return;
            }
            const p = pendingChange;
            if (p) {
              const changeResult = await changeShift(
                p.newShiftId, p.ci, p.day, p.currentShiftId,
                p.station_id, p.position_id, p.workarea_id,
                () => {}, () => {}
              );
              if (changeResult?.info?.status === 409 && changeResult.info.blockedCalendars?.length > 0) {
                showBlockedCalendars(
                  changeResult.info.blockedCalendars,
                  "No se pudo cambiar el turno porque hay calendarios cerrados para ese empleado en la fecha seleccionada."
                );
              } else if (changeResult?.ok) {
                setPendingChange(null);
              }
            }
          } catch {
            enqueueSnackbar("No se pudo resolver el conflicto", { variant: "error" });
          } finally {
            setConflictOpen(false);
            setConflictPayload(null);
            setInFlight(false);
            stopBusy();
          }
        }}
        onCancel={() => {
          setConflictOpen(false);
          setConflictPayload(null);
        }}
      />

      <LicenceModal
        open={licenseModalOpen}
        onClose={() => setLicenseModalOpen(false)}
        ci={ci}
        station_id={station_id}
        position_id={effectivePositionId}
        day={day}
      />

      <OvertimeModal
        open={overtimeModalOpen}
        onClose={() => setOvertimeModalOpen(false)}
        ci={ci}
        station_id={station_id}
        day={day}
        employeeName={`${name} ${surname}`.trim()}
      />

      <Dialog
        open={blockedCalendars.length > 0}
        onClose={closeBlockedCalendarsDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Calendarios cerrados implicados</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {blockedCalendarsMessage || "La operación no se pudo completar porque hay calendarios cerrados implicados."}
          </Alert>
          <List dense>
            {blockedCalendars.map((calendar, index) => (
              <ListItem key={`${calendar.calendar_id || index}`}>
                <ListItemText
                  primary={formatCalendarLabel(calendar)}
                  secondary={formatCalendarSecondary(calendar)}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBlockedCalendarsDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={implicatedCalendars.length > 0}
        onClose={confirmingImplicated ? undefined : closeImplicatedCalendarsDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Calendario activo implicado</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {implicatedCalendarsMessage || "El empleado tiene un calendario activo implicado. ¿Deseas continuar?"}
          </Alert>
          <List dense>
            {implicatedCalendars.map((calendar, index) => (
              <ListItem key={`${calendar.calendar_id || index}`}>
                <ListItemText
                  primary={formatCalendarLabel(calendar)}
                  secondary={formatCalendarSecondary(calendar)}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImplicatedCalendarsDialog} disabled={confirmingImplicated}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmImplicatedCalendars}
            variant="contained"
            color="warning"
            disabled={confirmingImplicated}
            startIcon={confirmingImplicated ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Sí, continuar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CellMenu;
